import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Lock, Monitor, Users, Calendar, Pill, BarChart3, Shield, Smartphone, Clipboard, Zap, Building2 } from "lucide-react";

// Icon mapping function
const getIconComponent = (iconName, color) => {
  const iconProps = { size: 24, color };
  const icons = {
    lock: <Lock {...iconProps} />,
    monitor: <Monitor {...iconProps} />,
    users: <Users {...iconProps} />,
    calendar: <Calendar {...iconProps} />,
    pill: <Pill {...iconProps} />,
    barchart: <BarChart3 {...iconProps} />,
    shield: <Shield {...iconProps} />,
    smartphone: <Smartphone {...iconProps} />,
    clipboard: <Clipboard {...iconProps} />,
    zap: <Zap {...iconProps} />,
    building: <Building2 {...iconProps} />,
  };
  return icons[iconName] || icons.lock;
};

const featuresList = [
  {
    titleEn: "Authentication & Security",
    titleFr: "Authentification et Sécurité",
    titleAr: "المصادقة والأمان",
    accent: "#3B82F6",
    accentLight: "#EFF6FF",
    iconName: "lock",
    descEn: "Raqib uses a multi-layer authentication system combining JWT tokens, refresh token validation, and two-factor authentication to ensure only authorized users can access the system.",
    descFr: "Raqib utilise un système d'authentification multicouche combinant les jetons JWT, la validation des jetons de rafraîchissement et l'authentification à deux facteurs pour garantir que seuls les utilisateurs autorisés peuvent accéder au système.",
    descAr: "يستخدم Raqib نظام مصادقة متعدد الطبقات يجمع بين رموز JWT والتحقق من رموز التحديث والمصادقة الثنائية لضمان أن يتمكن المستخدمون المرخصون فقط من الوصول إلى النظام.",
    benefitsEn: [
      "Your clinic data is protected with enterprise-grade security",
      "Staff members can only access information they're authorized to view",
      "Automatic session timeout prevents unauthorized access from shared devices"
    ],
    benefitsFr: [
      "Les données de votre clinique sont protégées avec une sécurité de niveau entreprise",
      "Les membres du personnel ne peuvent accéder qu'aux informations qu'ils sont autorisés à consulter",
      "L'expiration automatique de la session empêche l'accès non autorisé à partir d'appareils partagés"
    ],
    benefitsAr: [
      "بيانات عيادتك محمية بأمان على مستوى المؤسسة",
      "يمكن لموظفي العيادة الوصول فقط إلى المعلومات المصرح لهم بعرضها",
      "انتهاء الجلسة التلقائي يمنع الوصول غير المصرح به من الأجهزة المشتركة"
    ]
  },
  {
    titleEn: "Device & Session Management",
    titleFr: "Gestion des Appareils et des Sessions",
    titleAr: "إدارة الأجهزة والجلسات",
    accent: "#6366F1",
    accentLight: "#EEF2FF",
    iconName: "monitor",
    descEn: "Every login is tracked and linked to a device. Users can monitor all active sessions and remotely log out any device, giving full control and visibility over account activity.",
    descFr: "Chaque connexion est suivi et lié à un appareil. Les utilisateurs peuvent surveiller toutes les sessions actives et se déconnecter à distance de n'importe quel appareil, offrant un contrôle et une visibilité totals sur l'activité du compte.",
    descAr: "يتم تتبع كل عملية تسجيل دخول وربطها بجهاز. يمكن للمستخدمين مراقبة جميع الجلسات النشطة وتسجيل الخروج عن بعد من أي جهاز، مما يعطيهم السيطرة الكاملة والرؤية على نشاط الحساب.",
    benefitsEn: [
      "Detect suspicious login attempts immediately",
      "Manage staff access across multiple clinic locations",
      "Revoke access instantly if a device is lost or stolen"
    ],
    benefitsFr: [
      "Détectez les tentatives de connexion suspectes instantanément",
      "Gérez l'accès du personnel dans plusieurs emplacements de clinique",
      "Révoquer l'accès instantanément si un appareil est perdu ou volé"
    ],
    benefitsAr: [
      "اكتشف محاولات تسجيل الدخول المريبة على الفور",
      "إدارة وصول الموظفين في عدة مواقع عيادة",
      "إلغاء الوصول على الفور إذا تم فقدان الجهاز أو سرقته"
    ]
  },
  {
    titleEn: "Patient Management",
    titleFr: "Gestion des Patients",
    titleAr: "إدارة المرضى",
    accent: "#0D9488",
    accentLight: "#F0FDFA",
    iconName: "users",
    descEn: "Easily manage patient records including medical conditions, notes, and history in a structured and accessible way.",
    descFr: "Gérez facilement les dossiers des patients, y compris les conditions médicales, les notes et les antécédents de manière structurée et accessible.",
    descAr: "قم بإدارة سجلات المرضى بسهولة، بما في ذلك الحالات الطبية والملاحظات والسجلات بطريقة منظمة وسهلة الوصول.",
    benefitsEn: [
      "Complete patient history available at a glance",
      "Add detailed medical notes during consultations",
      "Track patient progress over time with historical data"
    ],
    benefitsFr: [
      "Historique complet des patients disponible en un coup d'œil",
      "Ajouter des notes médicales détaillées lors des consultations",
      "Suivre la progression des patients au fil du temps avec les données historiques"
    ],
    benefitsAr: [
      "السجل الطبي الكامل للمريض متاح في لمحة",
      "إضافة ملاحظات طبية مفصلة أثناء الاستشارات",
      "تتبع تقدم المريض بمرور الوقت مع البيانات التاريخية"
    ]
  },
  {
    titleEn: "Appointment Scheduling",
    titleFr: "Planification des Rendez-vous",
    titleAr: "جدولة المواعيد",
    accent: "#9333EA",
    accentLight: "#FAF5FF",
    iconName: "calendar",
    descEn: "Schedule and manage appointments efficiently with full details including patient, doctor, reason, and status. View appointments by day, month, or year.",
    descFr: "Planifiez et gérez les rendez-vous efficacement avec tous les détails, y compris le patient, le médecin, la raison et le statut. Visualisez les rendez-vous par jour, mois ou année.",
    descAr: "قم بجدولة وإدارة المواعيد بكفاءة مع جميع التفاصيل، بما في ذلك المريض والطبيب والسبب والحالة. عرض المواعيد حسب اليوم أو الشهر أو السنة.",
    benefitsEn: [
      "Reduce no-shows with appointment reminders",
      "Prevent double-booking and scheduling conflicts",
      "View availability across all doctors and time slots instantly"
    ],
    benefitsFr: [
      "Réduire les absences avec les rappels de rendez-vous",
      "Empêcher la double réservation et les conflits de planification",
      "Voir la disponibilité de tous les médecins et créneaux instantanément"
    ],
    benefitsAr: [
      "تقليل عدم الحضور من خلال تذكيرات المواعيد",
      "منع الحجز المزدوج وتضارب الجدولة",
      "عرض التوفر لجميع الأطباء والفترات الزمنية على الفور"
    ]
  },
  {
    titleEn: "Medication Tracking",
    titleFr: "Suivi des Médicaments",
    titleAr: "تتبع الأدوية",
    accent: "#EC4899",
    accentLight: "#FDF2F8",
    iconName: "pill",
    descEn: "Keep track of prescribed medications and monitor patient treatment progress over time.",
    descFr: "Suivi des médicaments prescrits et surveillance de la progression du traitement des patients au fil du temps.",
    descAr: "مراقبة الأدوية الموصوفة ومراقبة تقدم العلاج للمريض بمرور الوقت.",
    benefitsEn: [
      "Record all prescribed medications with dosage details",
      "Track treatment outcomes and patient compliance",
      "Identify medication interactions and potential risks"
    ],
    benefitsFr: [
      "Enregistrer tous les médicaments prescrits avec les détails du dosage",
      "Suivre les résultats du traitement et la conformité des patients",
      "Identifier les interactions médicamenteuses et les risques potentiels"
    ],
    benefitsAr: [
      "تسجيل جميع الأدوية الموصوفة مع تفاصيل الجرعة",
      "تتبع نتائج العلاج والامتثال للمريض",
      "تحديد التفاعلات الدوائية والمخاطر المحتملة"
    ]
  },
  {
    titleEn: "Dashboard & Insights",
    titleFr: "Tableau de Bord et Insights",
    titleAr: "لوحة التحكم والرؤى",
    accent: "#F97316",
    accentLight: "#FFF7ED",
    iconName: "barchart",
    descEn: "Get a clear overview of your clinic with visual statistics and real-time data insights.",
    descFr: "Obtenez une vue d'ensemble claire de votre clinique avec des statistiques visuelles et des insights de données en temps réel.",
    descAr: "احصل على نظرة عامة واضحة على عيادتك من خلال الإحصائيات المرئية ورؤى البيانات المتعددة.",
    benefitsEn: [
      "Monitor key performance metrics at a glance",
      "Generate reports on clinic operations and patient trends",
      "Make data-driven decisions to improve clinic efficiency"
    ],
    benefitsFr: [
      "Surveiller les métriques de performance clés en un coup d'œil",
      "Générer des rapports sur les opérations de la clinique et les tendances des patients",
      "Prendre des décisions basées sur les données pour améliorer l'efficacité de la clinique"
    ],
    benefitsAr: [
      "مراقبة مقاييس الأداء الرئيسية في لمحة",
      "إنشاء تقارير حول عمليات العيادة واتجاهات المرضى",
      "اتخاذ قرارات تحكمها البيانات لتحسين كفاءة العيادة"
    ]
  },
  {
    titleEn: "System Protection",
    titleFr: "Protection du Système",
    titleAr: "حماية النظام",
    accent: "#EF4444",
    accentLight: "#FEF2F2",
    iconName: "shield",
    descEn: "Raqib includes advanced protection mechanisms such as login attempt limits and token blacklisting to prevent unauthorized access.",
    descFr: "Raqib inclut des mécanismes de protection avancés tels que les limites de tentatives de connexion et la mise en liste noire des jetons pour prévenir les accès non autorisés.",
    descAr: "يتضمن Raqib آليات حماية متقدمة مثل حدود محاولات تسجيل الدخول ووضع قائمة سوداء للرموز لمنع الوصول غير المرخص.",
    benefitsEn: [
      "Automatic blocking after multiple failed login attempts",
      "Protection against common security vulnerabilities",
      "Regular security updates and patches applied automatically"
    ],
    benefitsFr: [
      "Blocage automatique après plusieurs tentatives de connexion échouées",
      "Protection contre les vulnérabilités de sécurité courantes",
      "Les mises à jour et correctifs de sécurité réguliers sont appliqués automatiquement"
    ],
    benefitsAr: [
      "الحجب التلقائي بعد محاولات تسجيل دخول متعددة فاشلة",
      "الحماية من نقاط الضعف الأمنية الشائعة",
      "يتم تطبيق التحديثات والرقع الأمنية العادية تلقائيًا"
    ]
  }
];

const advantages = [
  { textEn: "Strong security (JWT + 2FA + session control)", textFr: "Sécurité robuste (JWT + 2FA + contrôle de session)", textAr: "أمان قوي (JWT + 2FA + التحكم في الجلسة)", iconName: "lock", color: "#60A5FA" },
  { textEn: "Full device and session visibility", textFr: "Visibilité complète des appareils et des sessions", textAr: "رؤية كاملة للأجهزة والجلسات", iconName: "smartphone", color: "#60A5FA" },
  { textEn: "Organized patient and appointment management", textFr: "Gestion organisée des patients et rendez-vous", textAr: "إدارة منظمة للمرضى والمواعيد", iconName: "clipboard", color: "#60A5FA" },
  { textEn: "Simple and clean workflow", textFr: "Processus de travail simple et propre", textAr: "سير عمل بسيط ونظيف", iconName: "zap", color: "#60A5FA" },
  { textEn: "Built for real clinic needs", textFr: "Conçu pour les besoins réels des cliniques", textAr: "مصمم لاحتياجات العيادات الحقيقية", iconName: "building", color: "#60A5FA" },
];

// Reusable fade-up animation wrapper
const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};



const FeatureCard = ({ feature, index, language }) => {
  const [expanded, setExpanded] = React.useState(false);

  const title = language === "ar" ? feature.titleAr : language === "fr" ? feature.titleFr : feature.titleEn;
  const description = language === "ar" ? feature.descAr : language === "fr" ? feature.descFr : feature.descEn;
  const benefits = language === "ar" ? feature.benefitsAr : language === "fr" ? feature.benefitsFr : feature.benefitsEn;
  const learnMoreText = language === "ar" ? "تعرف على المزيد" : language === "fr" ? "En savoir plus" : "Learn More";
  const showLessText = language === "ar" ? "أظهر أقل" : language === "fr" ? "Afficher moins" : "Show Less";

  return (
    <FadeUp delay={index * 0.1}>
      <motion.div
        whileHover={{ y: -8, boxShadow: `0 20px 40px ${feature.accent}20` }}
        className="h-full p-6 sm:p-8 rounded-3xl border transition-all flex flex-col"
        style={{
          background: feature.accentLight,
          borderColor: feature.accent + "30"
        }}
      >
        <motion.div
          whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.4 } }}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-5 shadow-md flex-shrink-0"
          style={{ background: "white", color: feature.accent, border: `2px solid ${feature.accent}` }}
        >
          {getIconComponent(feature.iconName, feature.accent)}
        </motion.div>

        <h3
          className="text-xl sm:text-2xl font-black mb-3 tracking-tight"
          style={{ color: feature.accent }}
        >
          {title}
        </h3>

        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-light flex-1">
          {description}
        </p>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 pt-4 border-t border-slate-300 border-opacity-50"
          >
            <p className="text-xs font-bold mb-3" style={{ color: feature.accent }}>
              {language === "ar" ? "الفوائد الرئيسية" : language === "fr" ? "Avantages clés" : "Key Benefits"}
            </p>
            <ul className="space-y-2">
              {benefits.map((benefit, idx) => (
                <motion.li
                  key={idx}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-slate-600 text-sm leading-relaxed font-light flex gap-2"
                >
                  <span style={{ color: feature.accent }} className="flex-shrink-0">✓</span>
                  <span>{benefit}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        <motion.button
          onClick={() => setExpanded(!expanded)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="w-full mt-4 py-2.5 rounded-lg font-semibold text-white transition-all text-sm flex-shrink-0"
          style={{ background: feature.accent }}
        >
          {expanded ? showLessText : learnMoreText}
        </motion.button>
      </motion.div>
    </FadeUp>
  );
};;

function Features() {
  const language = useSelector((state) => state.language);
  const isAr = language === "ar";

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      dir={isAr ? "rtl" : "ltr"}
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;0,900;1,400&family=DM+Serif+Display&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>

      {/* ─── Hero ─── */}
      <section
        ref={heroRef}
        className="relative overflow-hidden min-h-[80vh] sm:min-h-screen flex items-center justify-center px-4 py-24"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)" }}
      >
        {/* Animated background blobs */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.18, 0.28, 0.18] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, #3B82F6 0%, transparent 70%)" }}
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.12, 0.22, 0.12] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-[-15%] right-[-5%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, #6366F1 0%, transparent 70%)" }}
        />
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-black tracking-[0.25em] uppercase px-4 py-2 rounded-full mb-8 border"
            style={{ color: "#60A5FA", borderColor: "#60A5FA40", background: "#60A5FA10" }}
          >
            {language === "ar" ? "ميزات المنصة" : language === "fr" ? "Caractéristiques de la plateforme" : "Platform Features"}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-[1.05] tracking-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            {language === "ar" ? "إدارة عيادة" : language === "fr" ? "Gestion de clinique" : "Raqib Clinic"}<br />
            <span style={{ color: "#60A5FA" }}>
              {language === "ar" ? "راقب" : language === "fr" ? "Raqib" : "Management"}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed font-light"
          >
            {language === "ar"
              ? "نظام إدارة عيادة كامل مصمم لتبسيط العمليات وتأمين البيانات الحساسة وإعطاؤك السيطرة الكاملة على سير عملك."
              : language === "fr"
              ? "Un système complet de gestion de clinique conçu pour simplifier les opérations, sécuriser les données sensibles et vous donner un contrôle total sur votre flux de travail."
              : "A complete clinic management system designed to simplify operations, secure sensitive data, and give you full control over your workflow."}
          </motion.p>

          {/* Scrolling indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-16 flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2 text-slate-500"
            >
              <span className="text-xs tracking-widest uppercase font-semibold">
                {language === "ar" ? "اسحب" : language === "fr" ? "Défiler" : "Scroll"}
              </span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── Features ─── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {featuresList.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} language={language} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Key Advantages ─── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-slate-950">
        <div className="max-w-5xl mx-auto">
          <FadeUp className="text-center mb-14">
            <p className="text-xs font-black tracking-[0.25em] uppercase text-blue-400 mb-3">
              {language === "ar" ? "لماذا اختيارنا" : language === "fr" ? "Pourquoi Nous" : "Why Choose Us"}
            </p>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {language === "ar" ? "المزايا الرئيسية" : language === "fr" ? "Avantages clés" : "Key Advantages"}
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              {language === "ar" ? "لماذا Raqib هو الخيار الأمثل لعيادتك" : language === "fr" ? "Pourquoi Raqib est le bon choix pour votre clinique." : "Why Raqib is the right choice for your clinic."}
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {advantages.map((adv, i) => {
              const advText = isAr ? adv.textAr : language === "fr" ? adv.textFr : adv.textEn;
              return (
                <FadeUp key={i} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -4, boxShadow: "0 16px 40px rgba(59,130,246,0.15)" }}
                    transition={{ duration: 0.25 }}
                    className="flex items-start gap-4 p-5 sm:p-6 rounded-2xl border border-slate-800 bg-slate-900 group cursor-default"
                  >
                    <div className="flex-shrink-0 text-blue-400">
                      {getIconComponent(adv.iconName, adv.color)}
                    </div>
                    <p className="text-slate-300 font-medium text-sm sm:text-base leading-relaxed group-hover:text-white transition-colors">
                      {advText}
                    </p>
                  </motion.div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative overflow-hidden py-24 sm:py-32 px-4 text-center bg-slate-950">
        {/* Glow */}
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse, #3B82F620 0%, transparent 70%)" }}
        />

        <div className="relative z-10 max-w-2xl mx-auto">
          <FadeUp>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-5 tracking-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              {language === "ar"
                ? "هل أنت مستعد لتبسيط عملك؟"
                : language === "fr"
                ? "Prêt à Rationaliser votre Clinique?"
                : "Ready to Streamline Your Clinic?"}
            </h2>
            <p className="text-slate-400 text-base sm:text-lg mb-10 font-light">
              {language === "ar"
                ? "ابدأ في إدارة عيادتك بثقة وتحكم كامل."
                : language === "fr"
                ? "Commencez à gérer votre clinique en toute confiance et avec un contrôle total."
                : "Start managing your clinic with confidence and full control."}
            </p>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="relative inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-black text-white overflow-hidden"
              style={{ background: "linear-gradient(135deg, #3B82F6, #6366F1)" }}
            >
              <motion.span
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                  width: "60%",
                }}
              />
              {language === "ar" ? "ابدأ الآن" : language === "fr" ? "Commencer Maintenant" : "Get Started Now"}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </motion.button>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

export default Features;